import * as React from "react";
import * as Datetime from "react-datetime";
import * as moment from "moment";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave, faBan, faEdit } from "@fortawesome/free-solid-svg-icons";

const wrapPreventDefault = (fn: () => any) => (e: React.SyntheticEvent<any>) => {
    e.preventDefault();
    fn();
};

export interface ConditionalComponentProps {
    extraChecks?: () => boolean;
    children: React.ReactNode;
}
export const WhenEditable = ({ extraChecks, children }: ConditionalComponentProps) => <>{!extraChecks || extraChecks() ? children : null}</>;
export const WhenNotEditable = ({ extraChecks, children }: ConditionalComponentProps) => <>{!extraChecks || extraChecks() ? children : null}</>;
export const Conditional = ({
    whenEditable,
    whenNotEditable,
    children
}: {
    whenEditable?: () => boolean;
    whenNotEditable?: () => boolean;
    children: React.ReactNode;
}) => (
    <>
        <WhenEditable extraChecks={whenEditable}>{children}</WhenEditable>
        <WhenNotEditable extraChecks={whenNotEditable}>{children}</WhenNotEditable>
    </>
);

export default class extends React.Component<
    {
        title?: string;
        hideTitleInEditButton?: boolean;
        hideEditButton?: boolean;
        editable: boolean;
        extraClassName?: string;
        onEdit: () => void;
        onCancel: () => void;
        onSave: () => void;
        showButtons?: boolean;
        otherHeaderButtons?: React.ReactNode;
    },
    {}
> {
    render() {
        const { title, editable, onEdit, hideTitleInEditButton: hideTitleInEditButton } = this.props;
        const showButtons = this.props.showButtons === true || this.props.showButtons === undefined;

        return (
            <div className={`card shadow edit-card ${editable ? "editable" : "read-only"} mb-3`}>
                <div className={"card-header" + " " + this.props.extraClassName}>
                    <div className="row mx-0 px-0 d-flex">
                        <div className="pt-1 pl-0">{title || ""}</div>
                        <div className="ml-auto pr-0 mr-0">
                            {showButtons && !editable && this.props.otherHeaderButtons}
                            {showButtons && !this.props.hideEditButton && !editable && (
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    data-toggle="button"
                                    aria-pressed="false"
                                    onClick={wrapPreventDefault(onEdit)}
                                >
                                    <FontAwesomeIcon icon={faEdit} className="mr-2" />
                                    Edit {hideTitleInEditButton ? "" : this.props.title} {/* // TODO Check Length of Page Names for this */}
                                </button>
                            )}
                            {showButtons && editable && <div>{this.saveAndEditButtons()}</div>}
                        </div>
                    </div>
                </div>
                <div className="card-body">{this.filterChildren()}</div>
                {showButtons && editable && <div className="card-footer text-right">{this.saveAndEditButtons()}</div>}
            </div>
        );
    }

    filterChildren = (children: any = this.props.children): any => {
        return React.Children.map(children, (child: any) => {
            if (child && child.type) {
                if (child.type === WhenEditable && !this.props.editable) {
                    return null;
                } else if (child.type === WhenNotEditable && this.props.editable) {
                    return null;
                }
            }

            // if (!React.isValidElement(child)) {
            //     return child;
            // }

            if (child && child.props && (child.props as any).children) {
                const childChildren = this.filterChildren((child.props as any).children);
                return React.cloneElement(child as any, {
                    children: childChildren
                });
            }

            return child;
        });
    };

    saveAndEditButtons = () => {
        const { onCancel, onSave } = this.props;
        return (
            <>
                <button
                    type="button"
                    className="btn btn-secondary btn-sm mr-2"
                    data-toggle="button"
                    aria-pressed="false"
                    onClick={wrapPreventDefault(onCancel)}
                >
                    <FontAwesomeIcon icon={faBan} className="mr-2" />
                    Cancel
                </button>
                <button
                    form="application-form"
                    type="button"
                    className="btn btn-primary btn-sm px-3"
                    data-toggle="button"
                    aria-pressed="false"
                    onClick={wrapPreventDefault(onSave)}
                >
                    <FontAwesomeIcon icon={faSave} className="mr-2" />
                    Save
                </button>
            </>
        );
    };
}

export const EditableSubCard = ({ label, children }: { label?: React.ReactNode; children?: React.ReactNode }) => {
    return (
        <div className="card mt-3">
            <div className="card-header">{label || ""}</div>
            <div className="card-body">{children}</div>
        </div>
    );
};

export const EditableInputGroup = ({
    label,
    className,
    isRequired,
    inputClassName,
    type,
    name,
    editable,
    editableValue,
    nonEditableValue,
    onChange,
    maxLength,
    smallText,
    icon
}: {
    label: string;
    className?: string;
    inputClassName?: string;
    icon?: React.ReactNode;
    type: string;
    name: string;
    editable: boolean;
    isRequired?: boolean;
    editableValue: string | undefined;
    nonEditableValue: string | undefined;
    smallText?: React.ReactChild;
    maxLength?: number;
    onChange?: (e: React.ChangeEvent<any>) => void;
}) => {
    return (
        <div className={`form-group ${className}`}>
            <label htmlFor={name}>
                {label} {isRequired && <span className="text-danger">*</span>}
            </label>

            {icon ? (
                <div className="input-group mb-2">
                    <div className="input-group-prepend">
                        <div className="input-group-text">{icon}</div>
                    </div>
                    <input
                        type={type}
                        name={name}
                        className={`form-control ${inputClassName} ` + (isRequired ? "" : "not-required")}
                        placeholder={editable ? "" : "-"}
                        disabled={!editable}
                        onChange={onChange}
                        value={editable ? editableValue || "" : nonEditableValue || ""}
                        readOnly={!editable}
                        required={isRequired}
                        maxLength={maxLength}
                    />
                </div>
            ) : (
                <input
                    type={type}
                    name={name}
                    className={`form-control ${inputClassName} ` + (isRequired ? "" : "not-required")}
                    placeholder={editable ? "" : "-"}
                    disabled={!editable}
                    onChange={onChange}
                    value={editable ? editableValue || "" : nonEditableValue || ""}
                    readOnly={!editable}
                    required={isRequired}
                    maxLength={maxLength}
                />
            )}
            {smallText && editable && <small className="form-text text-info">{smallText}</small>}
        </div>
    );
};

export const EditableTextareaGroup = ({
    label,
    className,
    isRequired,
    rows,
    inputClassName,
    name,
    editable,
    editableValue,
    nonEditableValue,
    onChange,
    onBlur
}: {
    label: string;
    className?: string;
    inputClassName?: string;
    name: string;
    rows: number;
    editable: boolean;
    isRequired?: boolean;
    editableValue: string | undefined;
    nonEditableValue: string | undefined;
    onChange: (e: React.ChangeEvent<any>) => void;
    onBlur?: () => void;
}) => {
    return (
        <div className={`form-group ${className}`}>
            <label htmlFor={name}>
                {label}: {isRequired && <span className="text-danger">*</span>}
            </label>
            <textarea
                name={name}
                rows={rows}
                className={`form-control ${inputClassName} ${isRequired ? "" : "not-required"} ${!editable && `disabled`}`}
                placeholder={editable ? "" : "-"}
                disabled={!editable}
                onChange={onChange}
                value={editable ? editableValue : nonEditableValue}
                readOnly={!editable}
                required={isRequired}
                onBlur={onBlur}
            />
        </div>
    );
};

export const EditableDateGroup = ({
    label,
    className,
    invalidFeedBack,
    isRequired,
    inputClassName,
    dateFormat,
    name,
    timeFormat,
    editable,
    editableValue,
    nonEditableValue,
    onChange,
    isValidDate
}: {
    label: string;
    className?: string;
    inputClassName?: string;
    dateFormat?: string | false;
    name: string;
    timeFormat?: string | boolean;
    invalidFeedBack?: string;
    editable: boolean;
    isRequired?: boolean;
    editableValue: string | Date | moment.Moment;
    nonEditableValue: string | Date | moment.Moment;
    isValidDate?: (currentDate: any) => any;
    onChange: (date: any) => void;
}) => {
    return (
        <div className={`form-group ${className}`}>
            <label htmlFor={name}>
                {label}: {isRequired && <span className="text-danger">*</span>}
            </label>
            <Datetime
                dateFormat={dateFormat || "YYYY-MM-DD"}
                inputProps={{
                    placeholder: "-",
                    disabled: !editable,
                    readOnly: !editable,
                    className: `form-control ${inputClassName} ` + (isRequired ? "" : "not-required")
                }}
                onChange={onChange}
                input={true}
                timeFormat={timeFormat || false}
                isValidDate={isValidDate}
                value={(editable ? editableValue : nonEditableValue) || ""}
                closeOnSelect={true}
            />
            {invalidFeedBack && <div className="invalid-feedback">{invalidFeedBack}</div>}
        </div>
    );
};

export const EditableSelectGroup = ({
    label,
    info,
    className,
    isRequired,
    inputClassName,
    name,
    editable,
    editableValue,
    nonEditableValue,
    onChange,
    children
}: {
    label: string;
    className?: string;
    inputClassName?: string;
    name: string;
    info?: string;
    editable: boolean;
    isRequired?: boolean;
    editableValue: number | string | undefined;
    nonEditableValue?: number | string | undefined;
    onChange: (e: React.ChangeEvent<any>) => void;
    children: React.ReactNode;
}) => {
    return (
        <div className={`form-group ${className}`}>
            <label htmlFor={name}>
                {label} {isRequired && <span className="text-danger">*</span>}
            </label>
            <select
                className={`form-control ${inputClassName} ` + (isRequired ? "" : "not-required")}
                name={name}
                disabled={!editable}
                onChange={onChange}
                placeholder={editable ? "" : "-"}
                value={editable ? editableValue : nonEditableValue}
                aria-describedby={info}
            >
                {children}
            </select>
            {info && <small className="form-text text-muted"> {info}</small>}
        </div>
    );
};



// WEBPACK FOOTER //
// ./src/components/common/EditableCard.tsx